import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useDailyMood } from '@/hooks/useDailyMood';
import { moodOptions, MoodType } from '@/data/moodActivities';
import MoodCard from './MoodCard';
import ActivitySuggestion from './ActivitySuggestion';
import GuestMoodPrompt from './GuestMoodPrompt';
import { useNavigate } from 'react-router-dom';
import AuthRedirectSystem from '@/utils/authRedirectSystem';

const PENDING_MOOD_KEY = 'ifindlife-pending-mood';

const MoodCheckInSection: React.FC = () => {
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [guestSelectedMood, setGuestSelectedMood] = useState<MoodType | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserId(data.session?.user?.id);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id);
    });
    return () => subscription.unsubscribe();
  }, []);

  // After login, check for pending mood and auto-select it
  useEffect(() => {
    if (!userId) return;
    const pendingMood = sessionStorage.getItem(PENDING_MOOD_KEY) as MoodType | null;
    if (pendingMood && moodOptions.some(m => m.type === pendingMood)) {
      sessionStorage.removeItem(PENDING_MOOD_KEY);
      selectMood(pendingMood);
      // Scroll to mood section
      setTimeout(() => {
        document.getElementById('mood-check-in')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [userId]);

  const { mood, activity, dismissed, selectMood, dismiss, resetMood } = useDailyMood(userId);

  const handleMoodSelect = (moodType: MoodType) => {
    if (!userId) {
      // Guest: show selected mood with login prompt
      setGuestSelectedMood(moodType);
      return;
    }
    selectMood(moodType);
  };

  const handleGuestLogin = () => {
    if (guestSelectedMood) {
      sessionStorage.setItem(PENDING_MOOD_KEY, guestSelectedMood);
    }
    AuthRedirectSystem.setRedirect({
      returnTo: '/#mood-check-in',
      action: 'navigate',
      params: { section: 'mood' },
      message: 'Welcome back! Here\'s your wellness activity.',
    });
    navigate('/user-login');
  };

  const handleGuestReset = () => {
    setGuestSelectedMood(null);
  };

  // Hide only if logged in AND dismissed
  if (userId && dismissed) return null;

  return (
    <section id="mood-check-in" className="py-16 bg-white">
      <div className="container max-w-3xl mx-auto px-4">
        {userId && mood && activity ? (
          <div className="space-y-4">
            <p className="text-center text-sm text-gray-500">
              We have a suggestion for you 💡
            </p>
            <ActivitySuggestion
              activity={activity}
              mood={mood}
              onDismiss={dismiss}
              onReset={resetMood}
            />
          </div>
        ) : guestSelectedMood && !userId ? (
          <div className="space-y-8">
            <div className="text-center mb-4">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                How Are You Feeling Today?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto text-center">
                Select your mood and we'll suggest a quick wellness activity
              </p>
            </div>
            <GuestMoodPrompt
              selectedMood={guestSelectedMood}
              onLogin={handleGuestLogin}
              onReset={handleGuestReset}
            />
          </div>
        ) : (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                How Are You Feeling Today?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto text-center">
                Select your mood and we'll suggest a quick wellness activity
              </p>
              {!userId && (
                <p className="text-sm text-gray-500 mt-2">
                  Log in to get a personalized activity suggestion
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto">
              {moodOptions.map((option) => (
                <MoodCard
                  key={option.type}
                  mood={option}
                  onSelect={() => handleMoodSelect(option.type)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MoodCheckInSection;
